/* eslint-disable prefer-template, arrow-body-style, no-param-reassign */
import * as API from 'es6/core/api';
import Logger from 'es6/services/logger';
import Quizzes from 'es6/controllers/apps/quizzes';

/**
 * Implementing subclasses must call the super constructor and provide an implementation
 * of 'showNextQuiz()' and 'quizFinished()'.
 */
export default class SequencerBase {

  /**
   * @param {StudyNode[]} studyItems array of all studyItems that may be presented in this session
   * @param {QuizTimer} quizTimer (may be null)
   */
  constructor(studyItems, isDiagnosticQuiz, quizTimer = null) {
    this.progress = 0;
    this.activeQuiz = null;
    this._isDiagnosticQuiz = isDiagnosticQuiz;
    this._quizTimer = quizTimer;
    this._initializeStudyItems(studyItems);
    this.totalItems = this._itemsWithQuizzesRemaining().length;
  }

  /**
   * Initiate a study session.
   *
   * @param sessionFinishedCallback
   */
  startSequence(sessionFinishedCallback, sessionProgressUpdatedCallback) {
    this._sessionFinishedCallback = sessionFinishedCallback;
    this._sessionProgressUpdatedCallback = sessionProgressUpdatedCallback;
    sessionProgressUpdatedCallback(this.progress);
    this.showNextQuiz();
  }

  /**
   * Ends the sequence, sending presentation results to the callback method.
   */
  sequenceFinished() {
    this.activeQuiz = null;
    this._sessionFinishedCallback(this._collectPresentationResults());
  }

  /**
   * Force the study session to end prematurely.
   *
   * @param finishedCallback Replaces the standard the standard sessionFinishedCallback.
   */
  forceFinishSequence(finishedCallback) {
    this.activeQuiz = null;
    finishedCallback(this._collectPresentationResults());
  }

  skipCurrentItem() {
    if (!this.activeQuiz) {
      Logger.warn('IntraSessionSequencer', 'Item skip attempted when no quiz is active.');
      return;
    }

    const skippedItem = this._studyItemFromQuiz(this.activeQuiz);
    skippedItem.skipped = true;
    skippedItem.sequencing.remainingQuizzes = [];

    API.postSkipItem(skippedItem.contentId).then(() => {
      Logger.info('Skipping', 'Skipped item ' + skippedItem.contentId, skippedItem);
    }).catch((reason) => {
      Logger.error('Skipping', reason, reason.stack);
    });

    // Move directly to next quiz (the current quiz result will not be saved).
    this._incrementProgress();
    this.showNextQuiz();
  }

  inProgress() {
    // In progress if we still have a quiz active.
    return !_.isNull(this.activeQuiz);
  }

  pauseSequence() {
    this.pause = true;
    this.activeQuiz.pause();
  }

  resumeSequence() {
    this.pause = false;
    this.activeQuiz.resume();
  }

  _initializeStudyItems(studyItems) {
    this._studyItems = studyItems;
    _.each(studyItems, (studyItem) => {
      // Build Quiz objects for any scheduled quizzes.
      const initiallyScheduledQuizzes = _.chain(studyItem.quizSequence)
        .map((quizName) => {
          return this._buildQuiz(studyItem, quizName); // TODO we shouldn't be building quizzes here, unnecessary overhead.
        })
        // Compact as any unknown quiz types will add undefined entries.
        .compact()
        .value();

      studyItem.sequencing = {
        remainingQuizzes:     initiallyScheduledQuizzes,
        presentedQuizzes:     [],
        possibleQuizTypes:    _.union(studyItem.extraQuizzes, _.difference(studyItem.quizSequence, ['Study'])),
        lastSeenAt:           -1,
        extraQuizzesInserted: 0,
      };
    });
  }

  _buildQuiz(studyItem, quizName) {
    const quizConstructor = Quizzes[quizName];
    if (quizConstructor) {
      return new quizConstructor(studyItem.content, studyItem.goalId, this._isDiagnosticQuiz, this.quizFinished.bind(this), this._quizTimer);
    } else {
      Logger.error('Session Loader', 'Unknown quiz type ' + quizName + '.');
      return null;
    }
  }

  _showNextQuizForItem(studyItem) {
    studyItem.sequencing.lastSeenAt = this.currentPresentationIndex;
    const quiz = studyItem.sequencing.remainingQuizzes.shift();
    this.activeQuiz = quiz;
    quiz.show();
  }

  /*
   * Increments session progress after a quiz has been shown based on the
   * expected number of quizzes remaining. Should be called once following
   * after each quiz presentation.
   */
  _incrementProgress() {
    const expectedRemaining = this._expectedRemainingQuizCount();

    // Increase progress proportionally to expected number of quizzes that remain
    this.progress += (100 - this.progress) / (expectedRemaining + 1);

    let visibleProgress = Math.ceil(this.progress);
    // But don't round progress up to 100%.
    if (expectedRemaining !== 0 && visibleProgress === 100) {
      visibleProgress = 99;
    }

    this._updateProgress(visibleProgress);
  }

  /*
   * Returns an estimate of the number of quizzes remaining in the session
   * which is used to update progress.
   */
  _expectedRemainingQuizCount() {
    return _.reduce(this._itemsWithQuizzesRemaining(), function (count, item) {
      return count + item.sequencing.remainingQuizzes.length;
    }, 0);
  }

  /*
   * Helper function which returns an array of studyItems which still have
   * scheduled quizzes remaining to be presented.
   */
  _itemsWithQuizzesRemaining(studyItems = this._studyItems) {
    return studyItems.filter(studyItem => {
      return studyItem.sequencing.remainingQuizzes.length > 0;
    });
  }

  /*
   * Helper function for looking up the studyItem that a quiz is testing.
   *
   * @returns {StudyNode}
   */
  _studyItemFromQuiz(quiz) {
    return this._studyItems.find(studyItem =>
      studyItem.contentId === quiz.getContentId() && studyItem.contentDomain === quiz.getContentDomain()
    );
  }

  /**
   * Updates the progress manager.
   *
   * @param {Number} progress
   */
  _updateProgress(progress) {
    this._sessionProgressUpdatedCallback(progress);
  }

  /**
   * Returns all the finalized quiz results from the session.
   *
   * @returns {QuizResult[]}
   */
  _collectPresentationResults() {
    return _.chain(this._studyItems)
      .map(item => item.sequencing.presentedQuizzes)
      .flatten()
      .map(quizResult => quizResult.getFinalResult())
      .flatten()      // Some quizzes (such as sentence quizzes, may return multiple results)
      .value();
  }
}
